import { AppShell } from '@mantine/core';
import LeftMenu from '@/components/left-menu';
import TopNavbar from '@/components/top-navbar';
import { setWebTitle } from '@/lib/format-utils';
import { localTenant } from '@/services/session-service';
import { useMenuState } from '@/hooks/use-menu-state';
import { createContext, useContext, useState, useEffect } from 'react';
import { Business } from '@turizi/shared';
import { useLocation } from 'react-router-dom';
import { useBusinesses } from '@/hooks/api-hooks';
import { BookingPaxsAssociationProvider } from '@/components/service-booking/booking-paxs-association-context';

interface ActiveBusinessContextType {
    activeBusiness: Business | null;
    setActiveBusiness: (business: Business | null) => void;
    businesses: Business[];
    isLoading: boolean;
}

const ActiveBusinessContext = createContext<ActiveBusinessContextType | undefined>(undefined);

export function useActiveBusiness() {
    const context = useContext(ActiveBusinessContext);
    if (!context) {
        throw new Error('useActiveBusiness must be used within an ActiveBusinessProvider');
    }
    return context;
}

export default function PagesWrapper({ children }: { children: React.ReactNode }): JSX.Element {
    const tenant = localTenant();
    const { isMobile, menuWidth } = useMenuState();

    setWebTitle(tenant?.name);

    const location = useLocation();

    // Parse business ID from URL path (e.g., /businesses/12)
    const match = location.pathname.match(/\/businesses\/(\d+)/);
    const urlBusinessId = match ? parseInt(match[1], 10) : null;

    const [activeBusiness, setActiveBusiness] = useState<Business | null>(null);

    // Query businesses
    const {
        data,
        isLoading
    } = useBusinesses({
        owned_by_me: true
    });
    const businesses = data?.data || [];

    // Sync active business with URL business ID or clear on specific list pages
    useEffect(() => {
        if (urlBusinessId) {
            const found = businesses.find(b => Number(b.id) === Number(urlBusinessId));
            if (found) {
                setActiveBusiness(found);
            }
        } else if (businesses.length === 1) {
            setActiveBusiness(businesses[0]);
        } else if (location.pathname === '/' || location.pathname === '/my-businesses' || location.pathname === '/businesses') {
            setActiveBusiness(null);
        }
    }, [urlBusinessId, location.pathname, businesses]);

    // maintenance mode
    if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                    <h1 className="text-2xl font-bold mb-4">System Maintenance</h1>
                    <p className="text-gray-600 mb-6">We're currently performing maintenance on our servers.</p>
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        )
    }

    return (
        <ActiveBusinessContext.Provider value={{ activeBusiness, setActiveBusiness, businesses, isLoading }}>
            <BookingPaxsAssociationProvider>
                <AppShell
                    header={{ height: 54 }}
                    navbar={{
                        width: menuWidth,
                        breakpoint: 'none',
                    }}
                >
                    <AppShell.Header className="print:hidden border-b border-gray-200 bg-white">
                        <TopNavbar />
                    </AppShell.Header>

                    {!isMobile && (
                        <AppShell.Navbar
                            withBorder={true}
                            className="print:hidden border-r border-gray-200/80 bg-white"
                            style={{
                                width: menuWidth,
                                transition: 'width 0.3s ease',
                                overflow: 'visible',
                            }}
                        >
                            <LeftMenu />
                        </AppShell.Navbar>
                    )}

                    <AppShell.Main className="bg-[#fcfbfa]">
                        {/* Mobile menu - rendered outside of AppShell.Navbar */}
                        {isMobile && <LeftMenu />}

                        <div className="p-4 mx-auto">
                            {children}
                        </div>
                    </AppShell.Main>
                </AppShell>
            </BookingPaxsAssociationProvider>
        </ActiveBusinessContext.Provider>
    );
};

